import liff from '@line/liff';

const LIFF_ID = import.meta.env.VITE_LIFF_ID || 'dummy-liff-id';

interface LiffUser {
  userId: string;
  displayName?: string;
  pictureUrl?: string;
}

let isInitialized = false;
let liffUser: LiffUser | null = null;

export async function initLiff(): Promise<void> {
  if (isInitialized) return;

  try {
    await liff.init({ liffId: LIFF_ID });
    isInitialized = true;

    // Get user profile if logged in
    if (liff.isLoggedIn()) {
      const profile = await liff.getProfile();
      liffUser = {
        userId: profile.userId,
        displayName: profile.displayName,
        pictureUrl: profile.pictureUrl,
      };
    }
  } catch (error) {
    console.error('LIFF initialization failed:', error);
    // Continue without LIFF (for testing outside LINE)
    isInitialized = true;
    if (import.meta.env.DEV) {
      liffUser = {
        userId: 'dev-user-123',
        displayName: 'Dev User',
      };
    }
  }
}

export function isInLiff(): boolean {
  return liff.isInClient();
}

export function isLoggedIn(): boolean {
  if (!isInitialized) return false;
  return liff.isLoggedIn();
}

export function login(): void {
  if (!isLoggedIn()) {
    liff.login();
  }
}

export function logout(): void {
  if (isLoggedIn()) {
    liff.logout();
    liffUser = null;
  }
}

export function getUser(): LiffUser | null {
  return liffUser;
}

export function getUserId(): string | null {
  return liffUser?.userId || null;
}

export function getDisplayName(): string | null {
  return liffUser?.displayName || null;
}

/** LINE access token for the current login; the API verifies it to establish identity. */
export function getAccessToken(): string | null {
  if (!isInitialized) return null;
  try {
    return liff.isLoggedIn() ? liff.getAccessToken() : null;
  } catch {
    return null;
  }
}

export function getPictureUrl(): string | null {
  return liffUser?.pictureUrl || null;
}

export async function sendMessage(message: string): Promise<void> {
  if (!liff.isInClient()) {
    console.log('Cannot send message outside LIFF');
    return;
  }

  try {
    await liff.sendMessages([
      {
        type: 'text',
        text: message,
      },
    ]);
  } catch (error) {
    console.error('Failed to send message:', error);
  }
}

export function closeWindow(): void {
  if (liff.isInClient()) {
    liff.closeWindow();
  }
}

export function openExternal(url: string): void {
  if (liff.isInClient()) {
    liff.openWindow({ url, external: true });
  } else {
    window.open(url, '_blank');
  }
}

export async function shareTargetPicker(message: any): Promise<void> {
  await liff.shareTargetPicker([message]);
}
